import React, { useState, useEffect, useRef } from 'react';
import { clientsApi } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Search, Building2, MapPin, Phone, FileText, Truck, Loader2 } from 'lucide-react';

const ClientSelectDialog = ({ open, onOpenChange, onConfirm, docType = 'transport', loading = false }) => {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [recipientName, setRecipientName] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [recipientCity, setRecipientCity] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    if (open) {
      fetchClients();
      setSelectedClient(null);
      setRecipientName('');
      setRecipientAddress('');
      setRecipientCity('');
      setRecipientPhone('');
      setSearch('');
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchClients(), 200);
    return () => clearTimeout(t);
  }, [search]);

  const fetchClients = async () => {
    try {
      const res = await clientsApi.getAll(search ? { search } : {});
      setClients(res.data);
    } catch { /* ignore */ }
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setRecipientName(client.name || '');
    setRecipientAddress(client.address || '');
    const cityParts = [client.postal_code, client.city].filter(Boolean).join(' ');
    setRecipientCity(cityParts);
    setRecipientPhone(client.phone || client.mobile || '');
  };

  const handleConfirm = () => {
    onConfirm({
      recipient_name: recipientName,
      recipient_address: recipientAddress,
      recipient_city: recipientCity,
      recipient_phone: recipientPhone,
      client_id: selectedClient?.id || null,
    });
  };

  const docLabel = docType === 'transport' ? 'Bon de Transport' : 'Bon de Livraison';
  const DocIcon = docType === 'transport' ? Truck : FileText;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-white font-heading flex items-center gap-2">
            <DocIcon className="w-5 h-5 text-amber-400" />
            {docLabel} — Destinataire
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-5 gap-4">
          {/* Left: Client search list */}
          <div className="col-span-3 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input ref={searchRef} placeholder="Rechercher un client..."
                value={search} onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-zinc-950 border-zinc-800 text-white" data-testid="client-search-input" />
            </div>
            <ScrollArea className="h-[340px]">
              <div className="space-y-1 pr-2">
                {clients.length === 0 ? (
                  <p className="text-zinc-500 text-sm text-center py-8">Aucun client trouvé</p>
                ) : clients.map(client => (
                  <div key={client.id}
                    onClick={() => handleSelectClient(client)}
                    className={`p-2.5 rounded-lg cursor-pointer transition-all ${
                      selectedClient?.id === client.id
                        ? 'bg-amber-500/15 border border-amber-500/40'
                        : 'bg-zinc-950/50 border border-transparent hover:border-zinc-700'
                    }`}
                    data-testid={`client-option-${client.id}`}>
                    <div className="flex items-center gap-2">
                      <Building2 className={`w-4 h-4 flex-shrink-0 ${selectedClient?.id === client.id ? 'text-amber-400' : 'text-zinc-600'}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-sm font-medium truncate">{client.name}</p>
                        <p className="text-zinc-500 text-xs truncate">
                          {[client.postal_code, client.city].filter(Boolean).join(' ') || client.email || '-'}
                        </p>
                      </div>
                      {client.code && (
                        <span className="text-[10px] font-mono text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded">{client.code}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Right: Recipient form (auto-filled or manual) */}
          <div className="col-span-2 space-y-3">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Coordonnées destinataire</p>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Nom / Raison sociale</Label>
              <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)}
                className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm" data-testid="recipient-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Adresse</Label>
              <Input value={recipientAddress} onChange={(e) => setRecipientAddress(e.target.value)}
                className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm" data-testid="recipient-address-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Code postal / Ville</Label>
              <Input value={recipientCity} onChange={(e) => setRecipientCity(e.target.value)}
                className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm" data-testid="recipient-city-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Téléphone</Label>
              <Input value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)}
                className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm" data-testid="recipient-phone-input" />
            </div>

            {selectedClient && (
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-2 mt-2">
                <p className="text-amber-400 text-[10px] font-semibold uppercase">Client sélectionné</p>
                <p className="text-white text-xs mt-1">{selectedClient.name}</p>
                {selectedClient.contact_name && (
                  <p className="text-zinc-400 text-[11px]">Contact : {selectedClient.contact_name}</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
          <p className="text-zinc-500 text-xs">
            {selectedClient ? 'Modifiable avant génération' : 'Sélectionnez un client ou remplissez manuellement'}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" className="border-zinc-700" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button onClick={handleConfirm} className="bg-amber-500 hover:bg-amber-400 text-black"
              disabled={loading} data-testid="confirm-generate-doc">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DocIcon className="w-4 h-4 mr-2" />}
              Générer le {docLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClientSelectDialog;
